/**
 * /accept-invitation?token=… — публічна сторінка прийняття запрошення.
 *
 * Чотири сценарії (за спецою 3.0.5 розд. 3.3):
 *
 *   A. Token валідний, user НЕ залогінений, email уже існує
 *      → «Увійдіть, щоб прийняти» з посиланням на /login?return_to=…
 *   B. Token валідний, user залогінений, email збігається з запрошеним
 *      → кнопка «Прийняти запрошення» → POST /invitations/accept.
 *   C. Token валідний, user НЕ залогінений, email НЕ існує (новий user)
 *      → форма «створити пароль» → POST /invitations/accept з password
 *        → auto-login (отримуємо email/password, робимо login → tokens).
 *   D. Token невалідний/expired/revoked → пояснення.
 *
 * Edge: token валідний, user залогінений, але інший email
 *      → попередження «логуйтеся під іншим email», кнопка logout+login.
 *
 * Маршрут публічний (без RequireAuth/RequireTenant). axios interceptor
 * НЕ додає X-Tenant-Id на /invitations/* — це робить backend.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  acceptInvitation,
  getInvitationInfo,
  type InvitationInfo,
} from '@/api/invitations';
import { getMe, login as apiLogin } from '@/api/auth';
import { useAuth, getAccessToken } from '@/auth/store';

export default function AcceptInvitationPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const token = params.get('token');

  const setTokens = useAuth((s) => s.setTokens);
  const setActiveTenant = useAuth((s) => s.setActiveTenant);

  // Якщо токена немає в URL — це теж сценарій D (broken link).
  const hasToken = Boolean(token);

  // GET /invitations/info — публічний preview.
  const info = useQuery<InvitationInfo>({
    queryKey: ['invitation-info', token],
    queryFn: () => getInvitationInfo(token!),
    enabled: hasToken,
    retry: false,
  });

  // GET /auth/me — щоб визначити чи user уже залогінений І хто це.
  // Запит виконується тільки якщо є access_token (інакше backend поверне 401).
  // ВАЖЛИВО: getMe тепер tenant-scoped (Порція 3.0.5.0) — потребує X-Tenant-Id.
  // Якщо user залогінений але не вибрав tenant — me поверне 400, тоді ми
  // вважаємо що його залогіненість для accept-сторінки не критична.
  const isLoggedIn = Boolean(getAccessToken());
  const me = useQuery({
    queryKey: ['me'],
    queryFn: getMe,
    enabled: isLoggedIn,
    retry: false,
  });

  // ============== Сценарій D: token невалідний/expired ====================
  if (!hasToken || info.isError) {
    return (
      <div className="login">
        <div className="card" style={{ width: 520, padding: '2rem', background: 'white' }}>
          <h2 style={{ marginTop: 0 }}>Запрошення недійсне</h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Можливі причини:
          </p>
          <ul style={{ color: 'var(--text-secondary)', paddingLeft: '1.2rem' }}>
            <li>Термін запрошення минув (діє 7 днів)</li>
            <li>Запрошення було відкликано</li>
            <li>Посилання пошкоджене або неповне</li>
          </ul>
          <p style={{ color: 'var(--text-secondary)' }}>
            Зверніться до того, хто вас запросив, щоб отримати нове.
          </p>
          <div style={{ marginTop: '1.5rem' }}>
            <Link to="/login">
              <button type="button">На головну</button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Loading info.
  if (info.isLoading || !info.data) {
    return (
      <div className="login">
        <div className="card" style={{ width: 480 }}>Завантаження…</div>
      </div>
    );
  }

  const inv = info.data;
  const myEmail = me.data?.email ?? null;
  const emailMatches = isLoggedIn && myEmail !== null && myEmail.toLowerCase() === inv.invited_email.toLowerCase();

  // ============== Edge: залогінений, але інший email ======================
  if (isLoggedIn && me.data && !emailMatches) {
    return (
      <div className="login">
        <div className="card" style={{ width: 520, padding: '2rem', background: 'white' }}>
          <h2 style={{ marginTop: 0 }}>Це запрошення — для іншого користувача</h2>
          <div style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
            <div>Запрошення для: <strong>{inv.invited_email}</strong></div>
            <div>Ви увійшли як: <strong>{myEmail}</strong></div>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>
            Щоб прийняти це запрошення, увійдіть під email{' '}
            <strong>{inv.invited_email}</strong>.
          </p>
          <div style={{ marginTop: '1.5rem' }}>
            <Link to={`/login?return_to=/accept-invitation?token=${encodeURIComponent(token!)}`}>
              <button type="button" className="primary">Увійти іншим email</button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ============== Сценарій A: token валідний, user не залогінений, існуючий
  if (!isLoggedIn && inv.user_exists) {
    return (
      <div className="login">
        <div className="card" style={{ width: 520, padding: '2rem', background: 'white' }}>
          <h2 style={{ marginTop: 0 }}>Запрошення до «{inv.tenant_name}»</h2>
          <div style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
            <div>Запрошений email: <strong>{inv.invited_email}</strong></div>
            <div>Роль у tenant'і: <strong>{inv.role_display}</strong></div>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>
            Щоб прийняти запрошення — увійдіть.
          </p>
          <div style={{ marginTop: '1.5rem' }}>
            <Link to={`/login?return_to=/accept-invitation?token=${encodeURIComponent(token!)}`}>
              <button type="button" className="primary">Увійти і прийняти</button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ============== Сценарій B або C — рендеримо через AcceptForm =========
  return (
    <AcceptForm
      token={token!}
      info={inv}
      isLoggedIn={isLoggedIn && emailMatches}
      setTokens={setTokens}
      setActiveTenant={setActiveTenant}
      navigate={navigate}
      invalidateMyTenants={() => queryClient.invalidateQueries({ queryKey: ['my-tenants'] })}
    />
  );
}

// =========================================================================
// AcceptForm — окремий компонент бо потрібна власна mutation.
// =========================================================================

interface AcceptFormProps {
  token: string;
  info: InvitationInfo;
  /** true — сценарій B (залогінений, email збігається); false — сценарій C
   *  (новий user, треба задати пароль). */
  isLoggedIn: boolean;
  setTokens: (a: string, r: string) => void;
  setActiveTenant: (id: string | null) => void;
  navigate: (path: string, opts?: { replace?: boolean }) => void;
  invalidateMyTenants: () => void;
}

function AcceptForm({
  token,
  info,
  isLoggedIn,
  setTokens,
  setActiveTenant,
  navigate,
  invalidateMyTenants,
}: AcceptFormProps) {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: async () => {
      // Для нового user'а — accept з password, потім login для отримання
      // токенів (accept-endpoint поки не повертає TokenPair).
      // Для існуючого — accept без password (він уже залогінений).
      await acceptInvitation({
        token,
        password: isLoggedIn ? undefined : password,
      });
      if (!isLoggedIn) {
        // Новий user: тільки що створили акаунт + пароль. Логінимося.
        // Свіжостворений user без 2FA → login завжди повертає токени напряму
        // (requires_2fa=false). Перевіряємо явно для type-safety.
        const res = await apiLogin(info.invited_email, password);
        if (res.access_token && res.refresh_token) {
          setTokens(res.access_token, res.refresh_token);
        }
      }
      return null;
    },
    onSuccess: () => {
      // Тепер user точно у tenant'і. Виставляємо активний tenant + redirect.
      setActiveTenant(info.tenant_id);
      invalidateMyTenants();
      navigate('/', { replace: true });
    },
    onError: (e: unknown) => {
      const err = e as AxiosError<{ detail?: string }>;
      setLocalError(err.response?.data?.detail ?? 'Не вдалося прийняти запрошення');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (!isLoggedIn) {
      if (password.length < 8) {
        setLocalError('Пароль має містити мінімум 8 символів');
        return;
      }
      if (password !== passwordConfirm) {
        setLocalError('Паролі не співпадають');
        return;
      }
    }
    accept.mutate();
  }

  return (
    <div className="login">
      <form onSubmit={onSubmit} style={{ width: 520, padding: '2rem', background: 'white' }}>
        <h2 style={{ marginTop: 0 }}>Запрошення до «{info.tenant_name}»</h2>
        <div style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
          <div>Email: <strong>{info.invited_email}</strong></div>
          <div>Роль: <strong>{info.role_display}</strong></div>
        </div>

        {!isLoggedIn && (
          <>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Створіть пароль для вашого нового акаунта.
            </p>
            <div className="field">
              <label>Пароль (мін. 8 символів)</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                autoFocus
              />
            </div>
            <div className="field">
              <label>Повторіть пароль</label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
          </>
        )}

        <button
          type="submit"
          className="primary"
          style={{ width: '100%' }}
          disabled={accept.isPending}
        >
          {accept.isPending
            ? 'Прийняття…'
            : isLoggedIn
            ? 'Прийняти запрошення'
            : 'Створити акаунт і приєднатися'}
        </button>

        {localError && <div className="error">{localError}</div>}
      </form>
    </div>
  );
}
